import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { MdVerified } from 'react-icons/md'
import { PiArrowSquareOut } from 'react-icons/pi'
import type { VerificationRecord } from '@/types/verification'
import { strings } from '@/strings'

interface VerificationExplanationModalProps {
    isOpen: boolean
    onClose: () => void
    /** When true, shows text explaining the verification is from a bridged AT Protocol account */
    isViaBridge?: boolean
    /** Custom verification records from trusted verifiers */
    customVerifications?: VerificationRecord[]
    /** When true, verification came only from custom verifiers (not native appview) */
    isCustomVerification?: boolean
    /** When true, the current user personally verified this account */
    isVerifiedByYou?: boolean
}

export function VerificationExplanationModal({
    isOpen,
    onClose,
    isViaBridge = false,
    customVerifications,
    isCustomVerification = false,
    isVerifiedByYou = false,
}: VerificationExplanationModalProps) {
    const overlayRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }

        if (isOpen) {
            document.addEventListener('keydown', handleEscape)
            document.body.style.overflow = 'hidden'
        }

        return () => {
            document.removeEventListener('keydown', handleEscape)
            document.body.style.overflow = 'unset'
        }
    }, [isOpen, onClose])

    if (!isOpen) return null

    const validVerifications = customVerifications?.filter(v => v.isValid) ?? []

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
            {/* Backdrop */}
            <div
                ref={overlayRef}
                className="absolute inset-0 bg-black/80 backdrop-blur-md transition-opacity"
                onClick={onClose}
            />

            {/* Modal Content - Sheet style on mobile, centered on desktop */}
            <div className="relative bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md overflow-hidden animate-in fade-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200">
                <div className="p-6">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-4">
                        <MdVerified className="w-10 h-10 text-blue-500" />
                        <h2 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            {strings.verification.verifiedAccountTitle}
                        </h2>
                    </div>

                    {/* Explanation */}
                    <div className="space-y-4 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                        {isViaBridge ? (
                            <>
                                <p className="font-medium text-text-primary-light dark:text-text-primary-dark">
                                    {strings.verification.viaBridgeExplanation}
                                </p>
                                <p>
                                    This ActivityPub account is bridged to the AT Protocol via
                                    Bridgy Fed, and the original AT Protocol account has been
                                    verified by a trusted verifier.
                                </p>
                                <p>
                                    Verification on the AT Protocol is decentralized and can be
                                    provided by multiple trusted sources. The blue checkmark
                                    indicates that the bridged account's identity has been confirmed
                                    by one or more verifiers.
                                </p>
                            </>
                        ) : isVerifiedByYou && !isCustomVerification ? (
                            <>
                                <p className="font-medium text-text-primary-light dark:text-text-primary-dark">
                                    {strings.verification.verifiedByYouExplanation}
                                </p>
                                <p>
                                    You've confirmed that this account belongs to who they claim to
                                    be. You can revoke this verification at any time from their
                                    profile.
                                </p>
                            </>
                        ) : isCustomVerification ? (
                            <>
                                <p className="font-medium text-text-primary-light dark:text-text-primary-dark">
                                    This account has been verified by
                                    {isVerifiedByYou ? ' you and' : ''} your trusted verifiers.
                                </p>
                                <p>
                                    {isVerifiedByYou
                                        ? "In addition to your own verification, one or more of your trusted verifiers has confirmed this account's identity."
                                        : "You have configured custom verification in Settings. This checkmark indicates that one or more of your chosen trusted verifiers has confirmed this account's identity."}
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="font-medium text-text-primary-light dark:text-text-primary-dark">
                                    {strings.verification.verifiedByTrustedVerifierExplanation}
                                </p>
                                <p>
                                    Verification on the AT Protocol is decentralized and can be
                                    provided by multiple trusted sources. The blue checkmark
                                    indicates that this account's identity has been confirmed by one
                                    or more verifiers.
                                </p>
                                <p>
                                    Unlike traditional social media, AT Protocol verification isn't
                                    controlled by a single company. Different organizations can act
                                    as verifiers, creating a more open and trustworthy verification
                                    system.
                                </p>
                            </>
                        )}

                        {/* Custom verifier details */}
                        {validVerifications.length > 0 && (
                            <div className="border-t border-border-light dark:border-border-dark pt-4">
                                <p className="font-medium text-text-primary-light dark:text-text-primary-dark mb-2">
                                    {strings.verification.verifiedByHeading}
                                </p>
                                <ul className="space-y-2">
                                    {validVerifications.map(v => (
                                        <li
                                            key={v.verifierDid}
                                            className="flex items-center justify-between"
                                        >
                                            <div className="flex items-center gap-2">
                                                <MdVerified className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                <span className="text-text-primary-light dark:text-text-primary-dark font-medium">
                                                    {v.verifierLabel}
                                                </span>
                                            </div>
                                            {v.createdAt && (
                                                <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                                                    {new Date(v.createdAt).toLocaleDateString()}
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Learn more link */}
                        <a
                            href="https://atproto.com/specs/handle"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline pt-2"
                        >
                            {strings.verification.learnMoreLink}
                            <PiArrowSquareOut className="w-4 h-4" />
                        </a>
                    </div>

                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="w-full mt-6 py-3 px-4 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg font-medium text-text-primary-light dark:text-text-primary-dark hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                        {strings.common.close}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    )
}
